import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { context: String, warningUrl: String }
  static targets = ["consentModal", "warningModal", "consentCheckboxes"]

  connect() {
    this.consentsCleared = false
  }

  async intercept(event) {
    if (this.consentsCleared) {
      this.consentsCleared = false
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()

    if (this.processing) return
    this.processing = true

    try {
      const data = await this.checkConsents()

      if (data.missing_consents.length > 0) {
        await this.showConsentModal(data.missing_consents)
      }

      if (data.needs_warning) {
        await this.showWarningModal()
      }

      this.consentsCleared = true
      event.target.closest("form").requestSubmit()
    } catch (e) {
      console.error("[consent-gate]", e)
    } finally {
      this.processing = false
    }
  }

  async checkConsents() {
    const response = await fetch(`/consentimientos/check?context=${this.contextValue}`, {
      headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
    })
    return response.json()
  }

  showConsentModal(missingConsents) {
    return new Promise((resolve) => {
      this.consentResolve = resolve

      const checkboxesHtml = missingConsents.map(c => `
        <label class="flex items-start gap-3 cursor-pointer group">
          <input type="checkbox" data-consent-type="${c.consent_type}" value="1"
                 class="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-[#00A86B] focus:ring-[#00A86B]/50">
          <span class="text-sm text-slate-300 group-hover:text-white">
            Acepto
            <a href="${c.document_url}" class="text-[#00A86B] underline hover:text-white"
               data-action="click->legal-document#open">${c.label}</a>
            <span class="text-slate-500">(versión ${c.current_version})</span>
          </span>
        </label>
      `).join("")

      this.consentCheckboxesTarget.innerHTML = checkboxesHtml
      this._showModal(this.consentModalTarget)
    })
  }

  async acceptConsents() {
    const checkboxes = this.consentCheckboxesTarget.querySelectorAll("input[type=checkbox]")
    const allChecked = Array.from(checkboxes).every(cb => cb.checked)

    if (!allChecked) {
      this.shakeUnchecked(checkboxes)
      return
    }

    const btn = this.consentModalTarget.querySelector("[data-action='consent-gate#acceptConsents']")
    btn.disabled = true
    btn.textContent = "Procesando..."

    const body = {}
    checkboxes.forEach(cb => { body[cb.dataset.consentType] = "1" })

    try {
      const response = await fetch("/consentimientos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ consents: body })
      })

      const data = await response.json()
      if (data.ok) {
        await this._hideModal(this.consentModalTarget)
        if (this.consentResolve) {
          this.consentResolve()
          this.consentResolve = null
        }
      } else {
        btn.disabled = false
        btn.textContent = "Aceptar y continuar"
      }
    } catch (e) {
      console.error("[consent-gate] accept error:", e)
      btn.disabled = false
      btn.textContent = "Aceptar y continuar"
    }
  }

  showWarningModal() {
    return new Promise((resolve) => {
      this.warningResolve = resolve
      this._showModal(this.warningModalTarget)
    })
  }

  async acknowledgeWarning() {
    const btn = this.warningModalTarget.querySelector("[data-action='consent-gate#acknowledgeWarning']")
    btn.disabled = true
    btn.textContent = "Procesando..."

    try {
      const response = await fetch(this.warningUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      const data = await response.json()
      if (data.ok) {
        await this._hideModal(this.warningModalTarget)
        if (this.warningResolve) {
          this.warningResolve()
          this.warningResolve = null
        }
      } else {
        btn.disabled = false
        btn.textContent = "Entiendo y deseo continuar"
      }
    } catch (e) {
      console.error("[consent-gate] warning error:", e)
      btn.disabled = false
      btn.textContent = "Entiendo y deseo continuar"
    }
  }

  shakeUnchecked(checkboxes) {
    checkboxes.forEach(cb => {
      if (!cb.checked) {
        const label = cb.closest("label")
        label.classList.add("ring-2", "ring-red-500", "rounded-lg", "p-1", "shake")
        setTimeout(() => label.classList.remove("ring-2", "ring-red-500", "rounded-lg", "p-1", "shake"), 2000)
      }
    })
  }

  // ── Modal animation helpers ──

  _showModal(modalEl) {
    const overlay = modalEl.querySelector("[data-role=overlay]")
    const panel = modalEl.querySelector("[data-role=panel]")
    modalEl.classList.remove("hidden")
    modalEl.classList.add("flex")
    modalEl.offsetHeight
    overlay?.classList.add("overlay-enter")
    overlay?.classList.remove("overlay-hidden")
    panel?.classList.add("panel-enter")
    panel?.classList.remove("panel-hidden")
  }

  _hideModal(modalEl) {
    return new Promise(resolve => {
      const overlay = modalEl.querySelector("[data-role=overlay]")
      const panel = modalEl.querySelector("[data-role=panel]")
      overlay?.classList.remove("overlay-enter")
      overlay?.classList.add("overlay-hidden")
      panel?.classList.remove("panel-enter")
      panel?.classList.add("panel-hidden")

      if (panel && !this.prefersReducedMotion) {
        panel.addEventListener("transitionend", () => {
          modalEl.classList.add("hidden")
          modalEl.classList.remove("flex")
          resolve()
        }, { once: true })
      } else {
        modalEl.classList.add("hidden")
        modalEl.classList.remove("flex")
        resolve()
      }
    })
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
